import { ThunkAction } from 'redux-thunk';
import queryString from 'query-string';
import axios from '../utils/axios';
import db from '../../database';
import {
  setCheckPassword,
  setCollection,
  setDownloadToken,
  setCollectionFetchStatus,
  ICollectionState
} from 'store/slices/collection';
import { setUserBrand, IUserBrand } from 'store/slices/userBrand';
import { setImages } from 'store/slices/images';
import { Api } from 'old-store/utils/API';
import { fetchBanners } from 'store/slices/banners/actions';
import { getErrorStatus } from 'helpers/network';

import { HttpStatus } from 'constants/statusCodes';
import {
  groupImagesBySizeType,
  fetchGlobalShopSettingsForCollection
} from 'modules/ScrShopNew/store/slices/shop/actions';
import { fetchProducts } from 'modules/ScrShopNew/store/slices/products/actions';

// old shop version
import {
  groupImagesBySizeType as oldGroupImagesBySizeType,
  fetchGlobalShopSettingsForCollectionAction as oldFetchGlobalShopSettingsForCollectionAction,
  fetchProductsAction as oldFetchProductsAction
} from 'modules/ScrShop/store/actions';

// Types
import { RootState } from 'old-store/store';
import { IImage } from 'old-store/types/gallery';
import { ICollection } from 'types/collection';

type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, any>;

interface ISiteData {
  _id: string;
  updated_at: string;
  data?: any;
  [key: string]: any;
}

interface ICollectionResponse {
  result: ICollection & {
    expired?: boolean;
    galleryAppsIoUrl?: string;
    signedWildCardUrl: string;
  };
}

interface IUserBrandResponse {
  result: IUserBrand;
}

interface IGetCollectionByIdResult {
  collection: ICollection;
  imagesData?: any;
  images: IImage[];
}

interface IStatisticsData {
  action: string;
  scope: string;
  collection_id?: string;
  [key: string]: any;
}

const saveInitialReferrer = (): void => {
  const currentSiteId = window.location.pathname.split('/')[1];
  const referrer = document?.referrer || window.frames?.top?.document?.referrer;

  window.localStorage.setItem(`initialReferrer-${currentSiteId}`, referrer || '');
};

export const checkIfSiteChanged =
  (site: ISiteData): AppThunk<Promise<ISiteData | undefined>> =>
  async (dispatch, getState) => {
    try {
      const { isApp } = getState().environment;
      let pwCorrect: string | undefined;
      if (!isApp) pwCorrect = queryString.parse(window.location.search).pwCorrect as string;

      const res = await axios.get<{ result: { data: ISiteData } }>('/api/site/checkChange', {
        params: {
          updated_at: site.updated_at,
          site_id: site._id,
          pwCorrect
        },
        timeout: 10000
      });

      const { data } = res.data.result;

      if (res.status === HttpStatus.OK) {
        db.sites.update(site._id, { data }).catch((e: any) => {
          if (e && e.inner) {
            console.log('DataBaseError checkIfSiteChanged status=200 inner:', e.inner, e);
          } else {
            console.log('DataBaseError checkIfSiteChanged status=200:', e);
          }
        });

        return data;
      }
    } catch (e: any) {
      const status = getErrorStatus(e);

      if (status === HttpStatus.Forbidden) {
        db.sites.delete(site._id).catch((err: any) => {
          if (err && err.inner) {
            console.log('DataBaseError checkIfSiteChanged status=403:', err.inner, err);
          } else {
            console.log('DataBaseError checkIfSiteChanged status=403:', err);
          }
        });

        dispatch(setCheckPassword(true));

        return undefined;
      }

      // not changed
      if (status === HttpStatus.NotModified) return site;
    }
  };

export const getDownloadToken = (): AppThunk => async (dispatch, getState) => {
  try {
    const {
      collection: { password },
      environment: { isApp, iosVersion }
    } = getState();

    if ((password && isApp && iosVersion && iosVersion >= 13) || (password && window.API_URL_DL)) {
      const res = await axios.get<{ downloadToken: string }>(
        `/api/download/downloadToken/${window.SITE_ID}`
      );

      dispatch(setDownloadToken(res.data.downloadToken));
    }
  } catch (e) {
    console.log(e);
  }
};

export const checkPassword =
  (site_id: string, password: string): AppThunk =>
  async () => {
    console.log('NOW WE CHECKING THE PASSWORD');
    try {
      saveInitialReferrer();

      window.location.href = `${
        window.API_URL
      }/api/site/check_password?site_id=${site_id}&password=${encodeURIComponent(
        password
      )}&redirectUrlAfterAuth=${encodeURIComponent(window.location.href)}`;
    } catch (e) {
      console.log(e);
    }
  };

export const logStatistics =
  (data: IStatisticsData): AppThunk =>
  (dispatch, getState) => {
    try {
      const {
        environment: { siteTracking }
      } = getState();

      if (!siteTracking) return;

      if (!data.collection_id) data.collection_id = window.SITE_ID; // eslint-disable-line

      axios.put('/api/statistic/collection', data);
    } catch (e) {
      console.log(e);
    }
  };

export const getCollectionFields =
  (fields: string[]): AppThunk<Promise<ICollection | undefined>> =>
  async (dispatch, getState) => {
    try {
      const { environment } = getState();
      const { isApp } = environment;
      const { SITE_ID } = window;
      const fieldsStr = fields.join(',');

      // kann entfernt werden, wenn web app manifest starturl enthält
      let pwCorrect: string | undefined;
      if (!isApp) pwCorrect = queryString.parse(window.location.search).pwCorrect as string;

      const { data } = await axios.get<ICollectionResponse>(`/api/collections/${SITE_ID}`, {
        params: { pwCorrect, fields: fieldsStr }
      });

      dispatch(setCollection(data.result));

      return data.result;
    } catch (e) {
      console.log('e', e);
    }
  };

// this can be moved back to the new store once App.js has been refactored
export const getCollectionById =
  (id: string): AppThunk<Promise<IGetCollectionByIdResult | null>> =>
  async (dispatch, getState) => {
    try {
      const { environment, collection: stateCollection } = getState();
      const { isApp } = environment || {};
      const imagesData = (environment as any)?.images;

      let pwCorrect: string | undefined;
      // kann entfernt werden, wenn web app manifest starturl enthält
      if (!isApp) pwCorrect = queryString.parse(window.location.search).pwCorrect as string;

      const { data: collection } = await axios.get<ICollectionResponse>(`/api/collections/${id}`, {
        params: { pwCorrect }
      });

      if (collection.result.expired && !window.location.href.includes('/expired')) {
        dispatch(setCollection(collection.result));
        window.location.href = `/${collection.result._id}/expired`;

        return null;
      }

      // we have to fetch banners before images for the proper layout building
      dispatch(fetchBanners());

      const { pathname } = window.location;

      const isPhotobookPreviewPage = pathname.includes('photobook/preview');
      const isNotSharePage = !pathname.includes('/share/');

      const isPreviewPhotobookPage = isPhotobookPreviewPage && isNotSharePage;

      const imagesMethod = isPreviewPhotobookPage ? 'getPreviewImages' : 'getImages';

      const images = await Api.Images[imagesMethod]();

      // fetch this in an async thunk or RTK query
      const { data: userBrands } = await axios.get<IUserBrandResponse>(
        `/api/collections/${id}/userbrand`
      );

      if (collection.result.galleryAppsIoUrl) {
        window.location.href = collection.result.galleryAppsIoUrl;

        return null;
      }

      db.sites.put({ id, data: collection.result }).catch((e: any) => {
        if (e && e.inner) {
          console.log('DataBaseError getSiteById status=200 inner:', e.inner, e);
        } else console.log('DataBaseError getSiteById status=200:', e);
      });

      dispatch(setUserBrand(userBrands.result));

      dispatch(setImages({ images, signedWildCardUrl: collection.result.signedWildCardUrl }));
      dispatch(setCollection(collection.result));

      if ((stateCollection as ICollectionState)?.collectionCreator?.featureFlags?.newShop) {
        // We need to know if the global shop is active to determine if we request products at all
        await dispatch(fetchGlobalShopSettingsForCollection());
        dispatch(fetchProducts({ collection_id: window.SITE_ID }));
        dispatch(groupImagesBySizeType());
      } else {
        (dispatch as any)(oldFetchGlobalShopSettingsForCollectionAction());
        (dispatch as any)(oldFetchProductsAction({ collection_id: window.SITE_ID }));
        (dispatch as any)(oldGroupImagesBySizeType());
      }

      return { collection: collection.result, imagesData, images };
    } catch (e: any) {
      if (getErrorStatus(e) === HttpStatus.Forbidden) {
        db.sites.delete(id).catch((err: any) => {
          if (err && err.inner) {
            console.log('DataBaseError getSiteById status=403 inner:', err.inner, err);
          } else {
            console.log('DataBaseError getSiteById status=403:', err);
          }
        });

        dispatch(setCheckPassword(true));
      }
    }

    return null;
  };

export const setCollectionFetchedStatus =
  (fetched: boolean): AppThunk =>
  (dispatch) => {
    try {
      dispatch(setCollectionFetchStatus(fetched));
    } catch (error) {
      console.log(error);
    }
  };
